import { priorityRank, SOUND_POLICY, type SoundId, type SoundPriority } from './types'

export interface VoiceAdmitContext {
  nowMs: number
  activeVoices: number
  maxGlobalVoices: number
  lastPlayedAt: Map<SoundId, number>
  simulationSpeed: number
  documentHidden: boolean
}

export interface VoiceAdmitResult {
  admit: boolean
  reason?: 'muted-path' | 'hidden' | 'cooldown' | 'polyphony' | 'throttle-3x'
}

const GLOBAL_MAX_VOICES = 12
const GLOBAL_MAX_VOICES_3X = 7

export function globalVoiceCap(simulationSpeed: number): number {
  return simulationSpeed >= 3 ? GLOBAL_MAX_VOICES_3X : GLOBAL_MAX_VOICES
}

/**
 * Pure voice-stealing / drop policy.
 * At 3x, tower shots are heavily throttled so combat does not become continuous noise.
 */
export function admitSound(
  id: SoundId,
  ctx: VoiceAdmitContext,
  options?: { force?: boolean }
): VoiceAdmitResult {
  if (ctx.documentHidden && !options?.force) {
    return { admit: false, reason: 'hidden' }
  }
  const policy = SOUND_POLICY[id]
  const last = ctx.lastPlayedAt.get(id) ?? -Infinity
  if (ctx.nowMs - last < policy.cooldownMs) {
    return { admit: false, reason: 'cooldown' }
  }

  if (ctx.simulationSpeed >= 3 && policy.priority === 'tower') {
    // Keep roughly 1/3 of tower shots at 3x.
    const slot = Math.floor(ctx.nowMs / 40)
    if (slot % 3 !== 0) {
      return { admit: false, reason: 'throttle-3x' }
    }
  }

  const cap = Math.min(ctx.maxGlobalVoices, globalVoiceCap(ctx.simulationSpeed))
  if (ctx.activeVoices >= cap) {
    if (priorityRank(policy.priority) < priorityRank('signature')) {
      return { admit: false, reason: 'polyphony' }
    }
    // High-priority may steal: caller stops a lower voice; we still admit.
  }
  return { admit: true }
}

export function shouldStealFor(priority: SoundPriority): boolean {
  return priorityRank(priority) >= priorityRank('signature')
}
